/**
 * BackgroundTaskRunner — scheduler and executor for background tasks.
 *
 * Features:
 * - Priority-ordered execution
 * - Trigger-based firing (app-start, periodic, visibility-change)
 * - Parallel-run protection (same task can't run concurrently)
 * - AbortController per task for clean cancellation on stop()
 * - Logs execution time for debugging
 *
 * Usage:
 *   const runner = new BackgroundTaskRunner()
 *   runner.register(myTask)
 *   runner.start()   // fires app-start tasks, sets up periodic + visibility
 *   runner.stop()    // stops all timers, aborts running tasks
 */

import { appLifecycleHub } from "@/shared/utils/platform/appLifecycleHub"

import type { BackgroundTaskDef } from "./types"

export class BackgroundTaskRunner {
  private tasks: BackgroundTaskDef[] = []
  private running = new Set<string>()
  private timers = new Map<string, ReturnType<typeof setInterval>>()
  private abortControllers = new Map<string, AbortController>()
  private started = false
  private unsubscribeForeground: (() => void) | null = null

  // ── Registration ──────────────────────────────────────────────────────

  register(task: BackgroundTaskDef): void {
    this.tasks.push(task)
    // Keep sorted by priority
    this.tasks.sort((a, b) => a.priority - b.priority)
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────

  start(): void {
    if (this.started) return
    this.started = true
    console.debug(
      "[BackgroundTaskRunner] Starting with",
      this.tasks.length,
      "tasks",
    )

    // Fire app-start triggers (respecting initialDelay)
    for (const task of this.tasks) {
      if (task.triggers.includes("app-start")) {
        const delay = task.initialDelayMs ?? 0
        if (delay > 0) {
          const timer = setTimeout(() => void this.runTask(task), delay)
          // Store as interval to simplify cleanup (clearTimeout works on both)
          this.timers.set(
            `${task.id}:start`,
            timer as unknown as ReturnType<typeof setInterval>,
          )
        } else {
          void this.runTask(task)
        }
      }
    }

    // Set up periodic triggers
    for (const task of this.tasks) {
      if (task.triggers.includes("periodic") && task.intervalMs) {
        const timer = setInterval(
          () => void this.runTask(task),
          task.intervalMs,
        )
        this.timers.set(`${task.id}:periodic`, timer)
      }
    }

    // Set up visibility-change trigger via AppLifecycleHub
    // (replaces raw document.visibilitychange — also handles native Capacitor resume)
    const visibilityTasks = this.tasks.filter((t) =>
      t.triggers.includes("visibility-change"),
    )
    if (visibilityTasks.length > 0) {
      this.unsubscribeForeground = appLifecycleHub.on("foreground", () => {
        for (const task of visibilityTasks) {
          void this.runTask(task)
        }
      })
    }
  }

  stop(): void {
    if (!this.started) return
    this.started = false
    console.debug("[BackgroundTaskRunner] Stopping")

    // Clear all timers
    for (const timer of this.timers.values()) {
      clearInterval(timer)
    }
    this.timers.clear()

    // Unsubscribe from AppLifecycleHub foreground events
    if (this.unsubscribeForeground) {
      this.unsubscribeForeground()
      this.unsubscribeForeground = null
    }

    // Abort all running tasks
    for (const controller of this.abortControllers.values()) {
      controller.abort()
    }
    this.abortControllers.clear()
    this.running.clear()
  }

  // ── Task execution ────────────────────────────────────────────────────

  private async runTask(task: BackgroundTaskDef): Promise<void> {
    // Skip if already running (parallel protection)
    if (this.running.has(task.id)) {
      console.debug(
        `[BackgroundTaskRunner] Skipping ${task.id} (already running)`,
      )
      return
    }

    this.running.add(task.id)
    const controller = new AbortController()
    this.abortControllers.set(task.id, controller)

    const t0 = performance.now()
    try {
      await task.run(controller.signal)
      const elapsed = Math.round(performance.now() - t0)
      console.debug(
        `[BackgroundTaskRunner] ${task.id} completed in ${elapsed}ms`,
      )
    } catch (error) {
      if ((error as Error)?.name === "AbortError") {
        console.debug(`[BackgroundTaskRunner] ${task.id} aborted`)
      } else {
        console.error(`[BackgroundTaskRunner] ${task.id} failed:`, error)
      }
    } finally {
      this.running.delete(task.id)
      this.abortControllers.delete(task.id)
    }
  }
}
